import React from "react";

export type CategoryTotals = { invoiced: number; paid: number };

export type AccountingTotals = {
  initiation: CategoryTotals;
  annualDues: CategoryTotals;
  assessmentDues: CategoryTotals;
  membershipBalance: CategoryTotals;
  specialFund: CategoryTotals;
  annualMeeting: CategoryTotals;
  lateFees: CategoryTotals;
};

export type AccountingStatsViewMode = "fiscalYear" | "total";

const PRIMARY = "#004b87";

const CATEGORY_ORDER: { key: keyof AccountingTotals; label: string }[] = [
  { key: "initiation", label: "Initiation Amount" },
  { key: "annualDues", label: "Annual Dues Amount" },
  { key: "specialFund", label: "Special Funds" },
  { key: "annualMeeting", label: "Annual Meeting Fee" },
  { key: "lateFees", label: "Late Fees" },
];

function formatAmount(value: number): string {
  return `$ ${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function AccountingStats({
  totals,
  viewMode,
  onViewModeChange,
}: {
  totals: AccountingTotals;
  viewMode: AccountingStatsViewMode;
  onViewModeChange: (mode: AccountingStatsViewMode) => void;
}): React.ReactElement {
  return (
    <div
      style={{
        margin: "0 auto",
        width: "100%",
        maxWidth: 1000,
        background: "#f4f6f8",
        borderRadius: 6,
        padding: "18px 24px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
        border: "1px solid #cbd5e1",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          borderBottom: "1px solid #e2e8f0",
          paddingBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: "bold",
            color: PRIMARY,
            textTransform: "uppercase",
          }}
        >
          Financial Summary Metrics
        </span>

        <div
          style={{
            display: "inline-flex",
            background: "#e2e8f0",
            padding: 2,
            borderRadius: 6,
          }}
        >
          <button
            type="button"
            onClick={() => onViewModeChange("fiscalYear")}
            style={{
              padding: "4px 12px",
              fontSize: 11,
              fontWeight: "bold",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              background: viewMode === "fiscalYear" ? PRIMARY : "transparent",
              color: viewMode === "fiscalYear" ? "#ffffff" : "#4a5568",
            }}
          >
            Fiscal Year
          </button>
          <button
            type="button"
            onClick={() => onViewModeChange("total")}
            style={{
              padding: "4px 12px",
              fontSize: 11,
              fontWeight: "bold",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              background: viewMode === "total" ? PRIMARY : "transparent",
              color: viewMode === "total" ? "#ffffff" : "#4a5568",
            }}
          >
            Total
          </button>
        </div>
      </div>

      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 12,
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                color: "#4a5568",
                fontWeight: "bold",
                textAlign: "left",
                padding: "6px 8px",
                fontSize: 11,
              }}
            >
              Category
            </th>
            {CATEGORY_ORDER.map((c) => (
              <th
                key={String(c.key)}
                style={{
                  color: "#4a5568",
                  fontWeight: "bold",
                  textAlign: "right",
                  padding: "6px 8px",
                  fontSize: 11,
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              style={{
                padding: "8px",
                textAlign: "left",
                fontWeight: "bold",
                color: "#2d3748",
              }}
            >
              Invoiced:
            </td>
            {CATEGORY_ORDER.map((c) => (
              <td
                key={String(c.key)}
                style={{
                  padding: "8px",
                  textAlign: "right",
                  fontWeight: 500,
                }}
              >
                {formatAmount(totals[c.key].invoiced)}
              </td>
            ))}
          </tr>
          <tr>
            <td
              style={{
                padding: "8px",
                textAlign: "left",
                fontWeight: "bold",
                color: "#2d3748",
              }}
            >
              Paid:
            </td>
            {CATEGORY_ORDER.map((c) => (
              <td
                key={String(c.key)}
                style={{
                  padding: "8px",
                  textAlign: "right",
                  fontWeight: 500,
                }}
              >
                {formatAmount(totals[c.key].paid)}
              </td>
            ))}
          </tr>
          <tr>
            <td
              style={{
                padding: "8px",
                textAlign: "left",
                fontWeight: "bold",
                color: PRIMARY,
                borderTop: "1px dashed #cbd5e1",
              }}
            >
              Balance:
            </td>
            {CATEGORY_ORDER.map((c) => (
              <td
                key={String(c.key)}
                style={{
                  padding: "8px",
                  textAlign: "right",
                  fontWeight: "bold",
                  color: PRIMARY,
                  borderTop: "1px dashed #cbd5e1",
                }}
              >
                {formatAmount(totals[c.key].invoiced - totals[c.key].paid)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
